import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch } from "../redux/store";
import { openTab, closeTab } from "../redux/actions/workspaceActions";
import {
  SIDEBAR_ITEMS,
  SidebarItem,
  SidebarEntry,
} from "../config/sidebarConfig";

// Define the shape of a WorkspaceTab to avoid circular dependencies if needed
export interface WorkspaceTab {
  id: string;
  title: string;
  path: string;
  componentKey: string;
}

const isSidebarItem = (entry: SidebarEntry): entry is SidebarItem => {
  return !("isHeader" in entry);
};

const useWorkspaceNavigation = (): {
  navigateToTab: (path: string) => void;
  closeCurrentAndNavigate: (
    fallbackPath: string,
    tabIdToClose?: string | null,
  ) => void;
} => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const getDynamicTabData = (
    path: string,
  ): Pick<WorkspaceTab, "title" | "componentKey"> | null => {
    if (path === "/purchase-orders/create") {
      return {
        title: "New Purchase Order",
        componentKey: "PURCHASE_ORDER_CREATE",
      };
    }

    const parts = path.split("/");
    if (parts.length >= 3) {
      const id = parts[2];
      const shortId = id.substring(0, 8);

      // Adapt dynamic routes based on your current project's needs
      if (path.startsWith("/student-fee-ledgers/")) {
        return {
          title: `Ledger #${shortId}...`,
          componentKey: "STUDENT_FEE_LEDGERS",
        };
      }
      if (path.startsWith("/assessments/")) {
        return {
          title: `Assessment #${shortId}...`,
          componentKey: "ASSESSMENTS",
        };
      }
      if (path.startsWith("/purchase-orders/")) {
        return {
          title: `P.Order #${shortId}...`,
          componentKey: "PURCHASE_ORDER",
        };
      }
      // Add more dynamic routes here as your app expands
    }

    return null;
  };

  const navigateToTab = (path: string): void => {
    const item = SIDEBAR_ITEMS.filter(isSidebarItem).find(
      (i) => i.path === path,
    );
    let tabData: WorkspaceTab | null = null;

    if (item && item.componentKey) {
      tabData = {
        id: item.path,
        title: item.title,
        path: item.path,
        componentKey: item.componentKey,
      };
    } else {
      const dynamicData = getDynamicTabData(path);
      if (dynamicData) {
        tabData = {
          id: path, // Treat the exact path as the unique tab ID
          title: dynamicData.title,
          path,
          componentKey: dynamicData.componentKey,
        };
      }
    }

    if (tabData) {
      dispatch(openTab(tabData));
    } else {
      console.warn(`No workspace mapping found for path: ${path}`);
    }

    navigate(path);
  };

  const closeCurrentAndNavigate = (
    fallbackPath: string,
    tabIdToClose: string | null = null,
  ): void => {
    const targetId = tabIdToClose ?? location.pathname;
    dispatch(closeTab(targetId));
    navigateToTab(fallbackPath);
  };

  return { navigateToTab, closeCurrentAndNavigate };
};

export default useWorkspaceNavigation;
